import { PayloadAction } from '@reduxjs/toolkit';
import { call, delay,fork, put, select, take, takeLatest } from 'redux-saga/effects';

import type { RootState } from '@/app/store';
import { selectUser } from '@/entities/user';
import { userActions } from '@/entities/user';
import type { CurrencyCode } from '@/shared/lib/currency';
import { twa } from '@/shared/lib/telegram/twa';

import { jettonApi } from '../api/jettonApi';
import {
  selectAutoplay,
  selectBetPanelState,
  selectIsAutoplayActive,
  selectJettonSelectedCurrency,
  selectJettonStatus,
} from './selectors';
import { jettonActions } from './slice';
import { JettonRound, JettonStatus } from './types';

function* placeBetSaga(
  action: PayloadAction<{ amount: number; currency: CurrencyCode; autoCashoutMultiplier?: number }>,
): Generator {
  const user = (yield select(selectUser)) as ReturnType<typeof selectUser>;
  if (!user) return;
  const { amount, currency, autoCashoutMultiplier } = action.payload;
  const { balance } = (yield select(selectBetPanelState)) as ReturnType<typeof selectBetPanelState>;
  if (amount > balance) return;

  try {
    const round = (yield call(
      jettonApi.placeBet,
      amount,
      currency,
      autoCashoutMultiplier,
    )) as JettonRound;
    yield put(jettonActions.setRound(round));
    yield put(userActions.updateBalanceByCurrency({ currency, amount: -amount }));
    twa.hapticFeedback.notificationOccurred('success');
  } catch {
    twa.hapticFeedback.notificationOccurred('error');
  }
}

function* cashoutSaga(): Generator {
  try {
    const result = (yield call(jettonApi.cashout)) as {
      cashoutMultiplier: number;
      payout: number;
      currency: CurrencyCode;
    };
    yield put(jettonActions.setMyCashout({ multiplier: result.cashoutMultiplier, payout: result.payout }));
    yield put(
      userActions.updateBalanceByCurrency({ currency: result.currency, amount: result.payout }),
    );
    twa.hapticFeedback.impactOccurred('medium');
  } catch {
    twa.hapticFeedback.notificationOccurred('error');
  }
}

function* cancelBetSaga(): Generator {
  try {
    const round = (yield call(jettonApi.cancelBet)) as JettonRound;
    yield put(jettonActions.setRound(round));
    twa.hapticFeedback.selectionChanged();
  } catch {
    twa.hapticFeedback.notificationOccurred('error');
  }
}

function* autoplaySaga(): Generator {
  while (true) {
    // Trigger on new waiting round OR immediately when user starts autoplay mid-wait
    yield take([jettonActions.wsWaiting.type, jettonActions.startAutoplay.type]);

    const isActive = (yield select(selectIsAutoplayActive)) as boolean;
    if (!isActive) continue;

    // If triggered by startAutoplay, only proceed if game is already in waiting phase
    const status = (yield select(selectJettonStatus)) as JettonStatus;
    if (status !== JettonStatus.WAITING) continue;

    // Natural delay so it doesn't look like a bot on the exact same frame
    yield delay(500 + Math.floor(Math.random() * 1000));

    // Re-check after delay — user might have stopped autoplay
    const stillActive = (yield select(selectIsAutoplayActive)) as boolean;
    if (!stillActive) continue;

    const selectedCurrency = (yield select(selectJettonSelectedCurrency)) as CurrencyCode;
    const balance =
      ((yield select(
        (s: RootState) => s.user.data?.wallets?.[selectedCurrency]?.balance ?? 0,
      )) as number);
    const { betAmount, cashoutMultiplier, numberOfGames, gamesPlayed } = (yield select(
      selectAutoplay,
    )) as ReturnType<typeof selectAutoplay>;

    if (balance < betAmount) {
      yield put(jettonActions.stopAutoplay());
      twa.hapticFeedback.notificationOccurred('error');
      continue;
    }

    yield put(
      jettonActions.placeBet({
        amount: betAmount,
        currency: selectedCurrency,
        autoCashoutMultiplier: cashoutMultiplier,
      }),
    );
    yield put(jettonActions.recordAutoplayGame());

    // Stop once the configured number of games has been reached.
    if (gamesPlayed + 1 >= numberOfGames) {
      yield put(jettonActions.stopAutoplay());
    }
  }
}

function* initJettonSaga(): Generator {
  try {
    const round = (yield call(jettonApi.getCurrentRound)) as JettonRound;
    yield put(jettonActions.setRound(round));
  } catch {
    // ignore, WS will sync
  }
}

export function* jettonWatcher(): Generator {
  yield takeLatest(jettonActions.placeBet.type, placeBetSaga);
  yield takeLatest(jettonActions.requestCashout.type, cashoutSaga);
  yield takeLatest(jettonActions.cancelBet.type, cancelBetSaga);
  yield fork(initJettonSaga);
  yield fork(autoplaySaga);
}
